export class Modal {
  /**
   * Controller for managing modal components on group pages. After passing a valid selector
   * for the main modal container into the constructor, you must call mount() to attach the required
   * event listeners.
   */

  /**
   * Requires a valid selector for the component's outermost containing element.
   */
  constructor(modalContainerSelector) {
    this.modalContainerSelector = modalContainerSelector;
    this.modalContainer = this.getModalContainer();
    this.modalType = this.modalContainer.dataset.modalController;
    this.modalController = this.getModalController(this.modalType);
    this.modalActivators = this.getModalActivators(this.modalType);
  }

  /**
   * Gets main controller for reviews modal
   * @returns {Element}
   */
  getModalController(type) {
    const controller = document.querySelector(
      `[data-modal-controller=${type}]`,
    );
    if (!controller) {
      throw new Error(`Unable to find main controller for ${type} modal.`);
    }
    return controller;
  }

  /**
   * Attempts to find the main modal container element
   *
   * @returns { Element }
   */
  getModalContainer() {
    const container = document.querySelector(this.modalContainerSelector);
    if (!container) {
      throw new Error(
        `Couldn't find element with class '${this.modalContainerSelector}.'`,
      );
    }
    return container;
  }

  /**
   * Attempts to collect all activators for the reviews modal
   *
   * @returns {NodeList<Element>} collection of modal activators
   */
  getModalActivators(type) {
    const activators = document.querySelectorAll(
      `[data-${type}-modal-activator]`,
    );
    if (!activators) {
      throw new Error(`Unable to find modal activators for ${type} modal.`);
    }
    return activators;
  }

  /**
   * Getter to determine whether the modal is active
   *
   * @returns { Boolean }
   */
  get modalActivated() {
    return this.modalContainer.dataset.modalActivated === 'true';
  }

  /**
   * Updates modal state
   * @param { Boolean } state - the new state of the modal
   */
  set modalActivated(state) {
    this.modalContainer.dataset.modalActivated = state;
  }

  /**
   * Blocks page scrolling
   *
   * @returns { this }
   */
  blockPageScroll() {
    if (document.body.classList.contains('group-page__block-scroll')) return;

    document.body.classList.add('group-page__block-scroll');
    return this;
  }

  /**
   * Enables page scrolling
   *
   * @returns { this }
   */
  enablePageScroll() {
    if (!document.body.classList.contains('group-page__block-scroll')) return;

    document.body.classList.remove('group-page__block-scroll');
    return this;
  }

  triggerModal() {
    const modalIsActive = this.modalActivated;

    this.modalActivated = (!this.modalActivated).toString();

    if (!modalIsActive) {
      this.blockPageScroll();
      return this;
    }

    this.enablePageScroll();

    return this;
  }

  mount() {
    if (this.modalActivators.length > 0) {
      this.modalActivators.forEach((activatorEl) => {
        activatorEl.addEventListener('click', (e) => {
          this.triggerModal();
        });
      });
    }

    return this;
  }
}
